<script lang="ts">
    import { onDestroy, onMount } from 'svelte'
    import { flip } from 'svelte/animate'
    import { writable } from 'svelte/store'
    import type { Writable } from 'svelte/store'

    import Icon from '@iconify/svelte'
    import { isEqual, orderBy } from 'lodash-es'
    import type { DateTime } from 'luxon'

    import { createDragHandle } from 'hydria/external/DragHandleZone'
    import { gettext as _, resolve, tgettext } from 'hydria/services/gettext'
    import { lg as currentLg, translatedSupportedLanguages } from 'hydria/services/language'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch, groups as ugroups, type SimpleFetchResult } from 'hydria/services/session'
    import { sanchor, sgoto, spath, sreplace } from 'hydria/svelte_helpers'
    import * as rt from 'hydria/utils/route'
    import { querystring } from 'hydria/utils/route'
    import { toLuxon } from 'hydria/utils/time'
    import { type as typeOf } from 'hydria/utils/type'

    import { Breadcrumb } from 'breadcrumb'

    import Date from 'hydria/components/Date.svelte'
    import GenericListLegend from 'hydria/components/GenericListLegend.svelte'

    let limit = 200

    const singular = 'page'
    const plural = 'pages'
    const baseUrl = '/pages'
    const jsonBaseUrl = `/json/cms/1/${plural}`

    const pageTitle = tgettext('Pages')
    const createButtonText = tgettext('Page')

    interface Props {
        genericDocumentTag?: string
    }

    let { genericDocumentTag: inGenericDocTag = $bindable(undefined) }: Props = $props()
    let genericDocumentTag = $derived(inGenericDocTag == null ? `${singular}-order` : inGenericDocTag)

    // Route parameters
    const mapping: rt.ISimpleMapping = {
        // q: 'string',
        skip: 'int1+',
    }
    const mappingOrder = ['q', 'skip']

    // Search options for the backend
    interface SOptions {
        // q: string
        skip: number
    }
    const defaults: SOptions = {
        // q: '',
        skip: 0,
    }

    type TLg = 'en' | 'fr'
    interface IRevision {
        id: string
        lg: TLg
        active: boolean
        ctime: DateTime
        author_id: string
        title: string
    }
    interface IRevisionGroup {
        nb_revs: number
        latest: IRevision
        online: IRevision

        mainTag?: 'online' | 'latest' | 'missing'
        main?: IRevision
    }
    type PageTypes = 'home' | 'generic'
    const specialPageTypes: PageTypes[] = ['home']
    const pageTypes = {
        home: {
            createButtonText: tgettext('Accueil'),
            name: tgettext('Accueil'),
        },
    }
    interface IDocument {
        id: string
        ctime: DateTime
        online_revisions: {
            id: string
            lg: TLg
        }[]
        lgs: {
            en: IRevisionGroup
            fr: IRevisionGroup
        }
        page_type: PageTypes
    }

    // Search result from the server
    interface ISearchResult {
        items: IDocument[]
        total_items: number
    }

    // search options; kept in sync with the query string (bi-directional)
    let sOptions: SOptions = $state(Object.assign({}, defaults))
    // search options parsed from the querystring
    let loadedOptions: SOptions = Object.assign({}, defaults)

    // Set to true after the *first* query string load
    let queryStringLoaded = false

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    // function setQueryString(inOptions: SOptions) {
    //     if (!queryStringLoaded) return

    //     if (isEqual(inOptions, loadedOptions)) return

    //     const options = { ...inOptions }

    //     if (options.skip && options.skip == loadedOptions.skip) {
    //         // Something non-skip must have changed, so we force skip to 0
    //         options.skip = 0
    //     }

    //     const qs = rt.simpleMapToQs(new Map(Object.entries(options)), rt.simpleReverseMap(mapping), rt.simpleParamSorter(mappingOrder)).toString()
    //     if (qs != querystring) {
    //         let path = qs == '' ? `${baseUrl}` : `${baseUrl}?${qs}`
    //         // If something other than skip has changed, we also want to enforce skip = 0
    //         sgoto(path)
    //     }
    // }
    function cleanIncomingQueryString(options: SOptions) {
        const qs = rt.simpleMapToQs(new Map(Object.entries(options)), rt.simpleReverseMap(mapping), rt.simpleParamSorter(mappingOrder)).toString()
        if (qs != $querystring) {
            let path = qs == '' ? `${baseUrl}` : `${baseUrl}?${qs}`
            // First time the page loads, and the parameters are incorrect.
            // (e.g. "q=", "skip=0", wrong parameter order, and so on)
            console.warn('parameters are dirty, replacing history from', $querystring, 'to', qs)
            sreplace(path)
        }
    }

    function loadQueryString(qs: string) {
        let sMap = rt.simpleParseQs(qs, rt.simpleMap(mapping))
        loadedOptions = Object.assign({}, defaults, Object.fromEntries(sMap))

        // So we only trigger the reactivity on true change, in case we are about to replace()
        if (!isEqual(sOptions, loadedOptions)) {
            sOptions = { ...loadedOptions }
        }

        // We also want to keep the URL clean.
        if (!queryStringLoaded) {
            cleanIncomingQueryString(loadedOptions)
        }
        // loadIntoForm()
        queryStringLoaded = true
        doSearch()
    }

    let results: Writable<IDocument[]> = writable([])

    let updating = $state(false)
    let loading = $state(true)
    let searchId = 0

    function doSearch() {
        updating = true

        let sdata: { [key: string]: any } = {}
        // if (sOptions.q != '') sdata.text = sOptions.q
        for (const [field, value] of Object.entries(sOptions)) {
            if (['q', 'skip'].includes(field)) continue
            switch (mapping[field]) {
                case '3state':
                case 'boolean':
                    if (value != null) sdata[field] = value
                    break
                case 'string':
                    if (value != null && value != '') sdata[field] = value
                    break
            }
        }

        if (sOptions.skip > 0) sdata.skip = sOptions.skip
        sdata.limit = limit
        // if (sOptions.s_by != '') sdata.sort_by = sOptions.s_by
        // sdata.sort_asc = !sOptions.s_desc

        searchId++
        ;((localSearchId) => {
            // simpleFetch('GET', jsonBaseUrl, { data: JSON.stringify(sdata) }).then((r) => {
            simpleFetch('GET', jsonBaseUrl, sdata)
                .then((r) => {
                    if (localSearchId != searchId) {
                        // This result does not match the current search anymore
                        // and there should be another search in progress
                        console.warn('Received search result for an older search, ignoring')
                        return
                    }
                    loadSearchResult(r.data as ISearchResult)
                    updating = false
                })
                .catch((e) => {
                    console.error('Error loading search results:', e)
                    notify({ title: $_('Search'), text: $_('Failed to load search results'), type: 'danger' })
                    updating = false
                })
        })(searchId)
    }

    function loadSearchResult(data: ISearchResult) {
        let litems = []

        for (const item of data.items) {
            if (item.ctime) item.ctime = toLuxon(item.ctime)

            for (const lg of ['en', 'fr']) {
                if (!item.lgs[lg]) {
                    item.lgs[lg] = {
                        mainTag: 'missing',
                        main: null,
                    }
                } else if (item.lgs[lg].online != null) {
                    item.lgs[lg].mainTag = 'online'
                    item.lgs[lg].main = item.lgs[lg].online
                } else if (item.lgs[lg].latest != null) {
                    item.lgs[lg].mainTag = 'latest'
                    item.lgs[lg].main = item.lgs[lg].latest
                } else {
                    item.lgs[lg].mainTag = 'missing'
                    item.lgs[lg].main = null
                }
            }

            litems.push(item)
        }

        results.set(litems)
        if (loading) loading = false

        if ($ugroups.has('dev')) {
            existingPageTypes = new Set($results.map((r) => r.page_type))
            missingSpecialPageTypes = new Set([...specialPageTypes].filter((r) => !existingPageTypes.has(r)))
            hasAllSpecialPages = missingSpecialPageTypes.size == 0
        } else {
            if (existingPageTypes.size) existingPageTypes = new Set()
            if (missingSpecialPageTypes.size) missingSpecialPageTypes = new Set()
            hasAllSpecialPages = false
        }

        sortResults()
    }

    let creating = $state(false)
    async function createNew() {
        creating = true
        try {
            const r = await simpleFetch('POST', jsonBaseUrl)
            sgoto(`/${singular.replace('_', '-')}/${r.data.doc_id}/${$currentLg}`)
        } catch (e) {
            console.error('Failed to create document:', e)
            notify({
                type: 'danger',
                title: $_('New document'),
                text: `Un nouveau document de type ${singular} n’a pas pu être créé.`,
            })
            creating = false
        }
    }

    async function createSpecialPage(pageType: PageTypes) {
        creating = true
        try {
            const r = await simpleFetch('POST', jsonBaseUrl, { page_type: pageType })
            sgoto(`/${singular.replace('_', '-')}/${r.data.doc_id}/${$currentLg}`)
        } catch (e) {
            console.error('Failed to create document:', e)
            notify({
                type: 'danger',
                title: $_('New document'),
                text: `Un nouveau document de type ${singular} n’a pas pu être créé.`,
            })
            creating = false
        }
    }

    let orderLoaded = $state(false)

    const savedOrder: Writable<Array<string>> = writable([])
    const resultOrder: Writable<Array<string>> = writable([])

    async function loadOrder() {
        let r: SimpleFetchResult
        try {
            r = await simpleFetch('GET', `/json/generic-doc/1/${genericDocumentTag}`)
        } catch (e) {
            console.error('Failed to load document order:', e)
            $resultOrder = []
            orderLoaded = true
            return
        }

        $savedOrder = r.data.item && typeOf(r.data.item.ids) == 'array' ? r.data.item.ids : []
        $resultOrder = [...$savedOrder]
        orderLoaded = true
        sortResults()
    }

    let savingOrder = $state(false)
    async function saveOrder() {
        let r: SimpleFetchResult
        savingOrder = true
        const orderToSave = [...$resultOrder]
        try {
            r = await simpleFetch('PUT', `/json/generic-doc/1/${genericDocumentTag}`, { item: { ids: orderToSave } })
        } catch (e) {
            savingOrder = false
            console.error('Failed to save order:', e)
            return
        }

        $savedOrder = orderToSave
        savingOrder = false
    }
    function revertOrder() {
        $resultOrder = [...$savedOrder]
        sortResults()
    }

    const sortedResults: Writable<IDocument[]> = writable([])
    const unsortableResults: Writable<IDocument[]> = writable([])

    function sortResults() {
        const ro = $resultOrder
        $sortedResults = orderBy(
            $results.filter((r) => r.page_type === 'generic'),
            [
                (item) => {
                    const i = ro.indexOf(item.id)
                    return i >= 0 ? i : 9999
                },
            ],
            ['asc'],
        )
        $unsortableResults = $results.filter((r) => r.page_type !== 'generic')
    }

    const { dragHandleZone, dragHandle } = createDragHandle()
    const flipDurationMs = 250

    function handleSort(e: any) {
        const ids: string[] = e.detail.items.map((i: any) => i.id)
        $sortedResults = e.detail.items
    }

    function handleSortEnd(e: any) {
        const ids: string[] = e.detail.items.map((i: any) => i.id)
        $resultOrder = ids
        // saveOrder()
        sortResults()
    }

    const crumbs = [{ txt: pageTitle, url: `#${baseUrl}` }]

    let hasAllSpecialPages = $state(true)
    let existingPageTypes: Set<PageTypes> = $state(new Set())
    let missingSpecialPageTypes: Set<PageTypes> = $state(new Set())

    onMount(() => {
        loadOrder()
    })
    onMount(() => {
        unsubs.push(
            querystring.subscribe(($qs) => {
                loadQueryString($qs ? $qs : '')
            }),
        )
    })

    let orderChanged = $derived(!isEqual($savedOrder, $resultOrder))
</script>

<style>
    .drag {
        width: 2em;
        text-align: center;
    }
</style>

<Breadcrumb {crumbs} />

{#if loading || !orderLoaded}
    <div class="card">
        <div class="card-body">
            <div class="m-4 d-flex justify-content-center align-items-center">
                <div class="spinner-border me-3" role="status" aria-hidden="true"></div>
            </div>
        </div>
    </div>
{:else}
    <div class="card mb-3">
        <div class="card-header text-bg-primary">
            <div class="d-flex align-items-center">
                <div class="flex-grow-1">{$_('Pages spéciales')}</div>
                <div>
                    {#if $ugroups.has('dev') && !hasAllSpecialPages}
                        {#if creating}
                            <button class="btn btn-outline-info btn-sm" disabled>
                                <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                                {$_('Un moment…')}
                            </button>
                        {:else}
                            {#each [...missingSpecialPageTypes] as pageType}
                                <button class="btn btn-danger btn-sm" type="button" onclick={() => createSpecialPage(pageType)} disabled={updating}>
                                    <Icon inline icon="mdi:file-document-plus-outline" />
                                    {$resolve(pageTypes[pageType].createText)}
                                </button>
                            {/each}
                        {/if}
                    {/if}
                </div>
            </div>
        </div>

        <div class="card-body p-0">
            <table class="table table-hover mb-0" class:disabled-look={updating}>
                <thead class="table-secondary">
                    <tr>
                        {#if $translatedSupportedLanguages.length > 1}
                            {#each $translatedSupportedLanguages as { lg, text }}
                                <th>{text}</th>
                            {/each}
                        {:else}
                            <th>{$_('Titre')}</th>
                        {/if}
                        <th>{$_('Type')}</th>
                    </tr>
                </thead>
                {#if !$unsortableResults.length}
                    <tbody>
                        <tr><td colspan="3" class="text-center">{$_('No results')}</td></tr>
                    </tbody>
                {:else}
                    <tbody>
                        {#each $unsortableResults as item (item.id)}
                            <tr>
                                {#each $translatedSupportedLanguages as { lg, text }}
                                    <td class:bg-warning={item.lgs[lg].mainTag != 'online'}>
                                        <Icon inline icon={item.lgs[lg].mainTag == 'online' ? 'mdi:checkbox-marked-outline' : 'mdi:checkbox-blank-outline'} />
                                        <a use:sanchor href={spath(`/${singular.replace('_', '-')}/${item.id}/${lg}`)}>{item.lgs[lg].main ? item.lgs[lg].main.title : $_('< missing >')}</a>
                                    </td>
                                {/each}
                                <td>{pageTypes[item.page_type] != null ? $resolve(pageTypes[item.page_type].name) : $_('Inconnu {type}', { type: item.page_type })}</td>
                            </tr>
                        {/each}
                    </tbody>
                {/if}
            </table>
        </div>
    </div>

    <div class="card">
        <div class="card-header text-bg-primary">
            <div class="d-flex align-items-center">
                <div class="flex-grow-1">{$resolve(pageTitle)}</div>
                <div>
                    {#if creating}
                        <button class="btn btn-outline-info btn-sm" disabled>
                            <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                            {$_('Un moment…')}
                        </button>
                    {:else}
                        <button class="btn btn-outline-light btn-sm" type="button" onclick={() => createNew()} disabled={updating}>
                            <Icon inline icon="mdi:file-document-plus-outline" />
                            {$resolve(createButtonText)}
                        </button>
                    {/if}
                </div>
            </div>
        </div>

        <div class="card-body p-0">
            <table class="table table-hover mb-0" class:disabled-look={updating}>
                <thead class="table-secondary">
                    <tr>
                        <th class="drag"><Icon icon="mdi:drag-horizontal-variant" inline /></th>
                        {#if $translatedSupportedLanguages.length > 1}
                            {#each $translatedSupportedLanguages as { lg, text }}
                                <th>{text}</th>
                            {/each}
                        {:else}
                            <th>{$_('Titre')}</th>
                        {/if}
                        <th>{$_('Created on')}</th>
                    </tr>
                </thead>
                {#if !$sortedResults.length}
                    <tbody>
                        <tr><td colspan="3" class="text-center">{$_('No results')}</td></tr>
                    </tbody>
                {:else}
                    <tbody use:dragHandleZone={{ items: $sortedResults, flipDurationMs: flipDurationMs }} onconsider={handleSort} onfinalize={handleSortEnd}>
                        {#each $sortedResults as item (item.id)}
                            <tr animate:flip={{ duration: flipDurationMs }}>
                                <td class="drag" use:dragHandle><Icon inline icon="mdi:drag-horizontal-variant" /></td>
                                {#each $translatedSupportedLanguages as { lg, text }}
                                    <td class:bg-warning={item.lgs[lg].mainTag != 'online'}>
                                        <Icon inline icon={item.lgs[lg].mainTag == 'online' ? 'mdi:checkbox-marked-outline' : 'mdi:checkbox-blank-outline'} />
                                        <a use:sanchor href={spath(`/${singular.replace('_', '-')}/${item.id}/${lg}`)}>{item.lgs[lg].main ? item.lgs[lg].main.title : $_('< missing >')}</a>
                                    </td>
                                {/each}
                                <td><Date value={item.ctime} format="h-table-s" /></td>
                            </tr>
                        {/each}
                    </tbody>
                {/if}
                <tfoot>
                    <tr>
                        <td colspan="4">
                            <div class="btn-group btn-group" role="group">
                                <button type="button" class="btn btn-outline-secondary" disabled>{$_('Ordre :')}</button>
                                <button type="button" class="btn btn-outline-secondary" disabled={!orderChanged} onclick={() => revertOrder()}><Icon inline icon="mdi:undo" /></button>
                                <button type="button" class="btn btn-primary" disabled={!orderChanged || savingOrder} onclick={() => saveOrder()}>
                                    {#if savingOrder}
                                        <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                                    {:else}
                                        <Icon inline icon="mdi:content-save" />
                                    {/if}
                                </button>
                            </div>
                        </td>
                    </tr>
                </tfoot>
            </table>
        </div>
    </div>

    <GenericListLegend showDragAndDrop={true} />
{/if}
